/**
 * Manage loading of async routes.
 *
 * This component is used to manage the loading of async routes. It is used by
 * TopLevelRoute and other route components. Additionally, it also manages
 * scroll restoration on navigation, ARIA live regions, and loading spinners.
 *
 * This component will get replaced when we upgrade to React Router v6, where
 * we can use features that they provide out of the box (or via simple components):
 *   * lazy (for dynamic imports of routes):
 *     https://reactrouter.com/en/main/route/lazy
 *   * useNavigation (for a global loading indicator):
 *     https://reactrouter.com/en/main/hooks/use-navigation
 *   * <ScrollRestoration /> (for scroll restoration):
 *     https://reactrouter.com/en/main/components/scroll-restoration
 *   * AccessibleNavigationAnnouncer:
 *     https://estevanmaito.me/blog/accessible-navigation-using-react-router
 *
 * You should not generally need to use this component directly, but instead
 * use the `TopLevelRoute` and associated components in order to build the
 * `app.jsx` routes.
 */
import * as React from "react";
import {useRouteMatch} from "react-router-dom";

import {useLocation} from "@khan/urls";
import {scrollToOffset} from "@khan/util-scrolling";

import RouteLoadSpinner from "./route-load-spinner.tsx";
import type {CommonRenderProps} from "./types.ts";

const AsyncRouteHandler = ({
    component,
    template,
}: CommonRenderProps): React.ReactElement => {
    const {pathname, search} = useLocation();
    const match = useRouteMatch();

    React.useEffect(() => {
        // Scroll to the top of the page on navigation.
        scrollToOffset(0, 0);
    }, [pathname]);

    const Component = component;

    return (
        <React.Suspense fallback={<RouteLoadSpinner template={template} />}>
            <Component
                // NOTE(john): We need to be able to get rid of these props if we
                // want to upgrade to React Router v6. Finding where this is being
                // used is going to be challenging, though!
                {...match.params}
                search={search}
                // NOTE(john): When we switch to React Router v6, we should adopt
                // something like this, instead:
                // https://estevanmaito.me/blog/accessible-navigation-using-react-router
                // https://www.gatsbyjs.com/blog/2020-02-10-accessible-client-side-routing-improvements/
                // Let screen reader knows that the component has loaded.
                // TODO(jeff, WEB-677): This should change. This is going
                // to make the entire content a "live" region that will announce
                // all its content changes. It also won't announce anything when
                // first rendered, most likely. Getting aria-live usage right
                // is hard but we likely want to have a separate unchanging
                // div that announces this content changing on navigation
                // instead of this.
                aria-live="polite"
            />
        </React.Suspense>
    );
};

export default AsyncRouteHandler;
